import { formatNumber } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, Input, LOCALE_ID, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, CommandBarComponent, IconCommand } from "@tylertech/commands-module";
import { BusyIndicatorComponent, formatAbbreviatedNumber, TruncateTextComponent } from "@tylertech/common-components-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { FileService, LoggingService } from "@tylertech/core-module";
import { ForgeCardModule, ForgeIconButtonModule, ForgeToolbarModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { Utilities } from "@tylertech/shared-module";
import { DepartmentViewModel, ExecutionResult, IChartCriteria, LookupCriteria, SettingLookupOption, } from "@tylertech/tyler-api-types";
import { ChartConfiguration, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { HRDepartmentLookupService } from "../../services";

@Component({
    selector: "payroll-sick-leave-chart",
    templateUrl: "./sickLeaveChart.tmpl.html",
    imports: [
        BusyIndicatorComponent,
        ChartJsContainerComponent,
        CommandBarComponent,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeCardModule,
        ForgeIconButtonModule,
        ForgeToolbarModule,
        ForgeTooltipModule,
        TruncateTextComponent,
    ]
})
export class SickLeaveChartComponent implements OnDestroy, OnInit
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $fileService = inject(FileService);
    private readonly $hrdepartmentService = inject(HRDepartmentLookupService);
    private readonly $commonDialogsService = inject(CommonDialogsService);
    private readonly $chartService = inject(ChartService);

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;

    private readonly destructor$ = new Subject<void>();

    private _chartCriteria: IChartCriteria;

    readonly commands: ReadonlyArray<Command>;
    readonly ChartTitle: string = "Leave Usage By Day";
    readonly showChart = signal(false);
    chartConfig: ChartConfiguration<"bar">;
    departmentIds: string[];
    departments: DepartmentViewModel[];
    isLoaded: boolean;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.ChartHasData,
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this.chartCriteria?.ChartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get ChartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get ChartSubtitle(): string
    {
        let ret = "Last twelve months ";
        if (!Utilities.IsNullOrUndefined(this.chartCriteria.Parameters))
        {
            const leaveDepartments = this.chartCriteria?.Parameters?.filter(x => x.Id === "LeaveDepartmentIds");
            if (leaveDepartments.length > 0 && !Utilities.IsNullOrUndefinedOrEmpty(leaveDepartments[0].Value))
            {
                this.departmentIds = leaveDepartments[0].Value.split(",");
            }
            else
            {
                this.departmentIds = null;
            }
            const leaveType = this.chartCriteria?.Parameters?.filter(x => x.Id === "LeaveTypeIds");
            if (leaveType.length > 0 && !Utilities.IsNullOrUndefinedOrEmpty(leaveType[0].Value))
            {
                const leaveTypeOptions = leaveType[0].Options;
                const leaveTypeIds = leaveType[0].Value.split(",");
                ret += " &bull; " + this.getLeaveTypeText(leaveTypeIds, leaveTypeOptions);
            }
            if (this.hasDepartments)
            {
                ret += " &bull; " + this.getDepartmentText();
            }
        }
        return ret;
    }

    get hasDepartments(): boolean
    {
        return (
            this.departmentIds &&
            this.departmentIds.length > 0 &&
            (!Utilities.IsNullOrUndefinedOrEmpty(this.departmentIds[0]) || this.departmentIds.length > 1) &&
            !Utilities.IsNullOrUndefined(this.departments)
        );
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const deptIds = this.departmentIds && this.departmentIds.join(",");
        if (deptIds && deptIds.length > 8000)
        {
            this.$commonDialogsService.showAlertMessage("Please Select Fewer Departments");
            return;
        }

        new Promise(
            (_resolveFn) =>
            {
                if (!this.hasData())
                {
                    return this.loadChartData();
                }

                this.updateChartConfiguration();
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
        this.$hrdepartmentService
            .Lookup(<LookupCriteria>{})
            .then((result: ExecutionResult<Array<DepartmentViewModel>>) =>
            {
                this.departments = result.Result;
            });
    }

    private getDepartmentName(id: string): string
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(id)) return null;
        const dept = this.departments.filter(x => x.Id === id)[0];
        return dept ? dept.Description : null;
    }

    private getDepartmentText(): string
    {
        let ret = "";
        if (this.hasDepartments)
        {
            this.departmentIds.forEach(id =>
            {
                const dept = this.getDepartmentName(id);
                if (dept)
                {
                    ret += ret.length ? ", " + dept : dept;
                }
            });
        }
        return ret;
    }

    private getLeaveTypeText(ids: string[], options: SettingLookupOption[]): string
    {
        let ret = "";
        ids.forEach(id =>
        {
            if (id !== "")
            {
                const display = options.filter(x => x.Value === id)[0].Display.split(" - ")[0];
                ret += ret.length ? ", " + display : display;
            }
        });
        return ret;
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService
            .GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if (!this.hasData())
                    {
                        return;
                    }

                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService
            .showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        const chartColor = this.$chartService.getColors(1)[0];

        this.chartConfig = {
            type: "bar",
            options: {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems: TooltipItem<"bar">[]) =>
                            {
                                return this.chartCriteria.ChartDatas[0].Labels[tooltipItems[0].dataIndex];
                            },
                            label: (tooltipItem: TooltipItem<"bar">): string | string[] =>
                            {
                                return formatNumber(tooltipItem.raw as number, this.locale);
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                    },
                    y: {
                        type: "linear",
                        display: true,
                        position: "right",
                        grid: { display: true },
                        beginAtZero: true,
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                },
            },
            data: {
                datasets: [
                    {
                        type: "bar",
                        data: this.chartCriteria.ChartDatas[0].Data,
                        backgroundColor: chartColor,
                        borderColor: chartColor,
                        hoverBackgroundColor: chartColor,
                        hoverBorderColor: chartColor,
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,
                    },
                ],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
